// apps/client/src/ui/screens/signIn.tsx (M0-T17) — the signedOut phase screen: 6-digit email code, or Apple when configured
import { tuning } from '@whiskerton/content';
import { useState } from 'react';
import { api } from '../../api/endpoints';
import { HttpError } from '../../api/http';
import { useGame } from '../../state/store';
import { Envelope, House } from '../hud/Icons';
import { useT } from '../i18n';
import { Button, Panel, TextField } from '../kit';
import { phaseScreen, type ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { signIn: undefined } }

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** The email step's notes (title polish, 2026-10-06): a line on how codes work and one on invite-only towns, each with a small icon in a
 *  leaf badge, so the left column says something useful instead of standing empty beside the field. */
function Note(p: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <li style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 10, alignItems: 'center' }}>
      <span className="note-badge" aria-hidden="true">{p.icon}</span>
      <span style={{ fontSize: 15, lineHeight: 1.35, fontWeight: 700, color: 'var(--wood-700)', textWrap: 'pretty' }}>{p.children}</span>
    </li>
  );
}
/** The invite code when this visit opened an /invite/<code> link (the towns phase shows its landing after sign-in). The preview API
 *  needs a session, so before sign-in only the code is known: no town or inviter name (M0 playtest note). */
function invitedCode(): string | null {
  if (typeof location === 'undefined') return null;
  const s = phaseScreen('towns', location.pathname);
  return s.id === 'invite' ? (s.params as { code: string }).code : null;
}
function SignIn() {
  const t = useT();
  const apple = useGame((s) => s.config?.apple ?? false);
  const [email, setEmail] = useState(''), [code, setCode] = useState(''), [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null);
  const send = async () => {
    if (!EMAIL.test(email.trim())) { setError(t('auth.invalidEmail')); return; }
    setBusy(true); setError(null);
    try { await api.sendCode(email.trim()); setSent(true); }
    catch (e) { setError(e instanceof HttpError && e.status === 429 ? t('errors.rate_limited') : t('errors.server_error')); }
    finally { setBusy(false); }
  };
  const signIn = async () => {
    setBusy(true); setError(null);
    try {
      await api.signInWithCode(email.trim(), code.trim());
      const me = await api.me();
      const s = useGame.getState();
      s.setMe(me);
      s.setPhase(me.profile ? 'towns' : 'needsProfile');
    } catch (e) {
      setError(e instanceof HttpError && e.status !== 401 && e.status < 500 && e.error !== 'invalid' ? t(`errors.${e.error}` as const) : t('auth.wrongCode'));
    } finally { setBusy(false); }
  };
  const startApple = async () => {
    try { await api.startApple(); } catch { setError(t('errors.server_error')); }
  };
  const invite = invitedCode();
  const p = { margin: 0 };
  // Two columns on a landscape phone (§8.7): what this is about (and, on the email step, how codes and towns work) on the left, the field
  // and its actions on the right, so the primary action and "Use a different email" never need a scroll. They stack when the panel is too
  // narrow for two.
  return (
    <div className="layer-full hit" style={{ display: 'grid', placeItems: 'center' }}>
      <Panel title={t('auth.title', { app: t('common.appName') })}>
        <div style={{ display: 'grid', gap: '14px 22px', width: 640, maxWidth: '100%', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 12 }}>
            {invite ? (
              <div style={{ display: 'grid', gap: 4, background: 'var(--leaf-100)', borderRadius: 16, padding: '12px 14px' }}>
                <strong style={{ fontWeight: 900, fontSize: 19, lineHeight: 1.2 }}>{t('auth.invited')}</strong>
                <span>{t('auth.invitedCode', { code: invite })}</span>
              </div>
            ) : <p style={p}>{t('auth.subtitle')}</p>}
            {!sent && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
                <Note icon={<Envelope />}>{t('auth.noteCode')}</Note>
                {!invite && <Note icon={<House />}>{t('auth.noteInvite')}</Note>}
              </ul>
            )}
            {sent && <>
              <p style={p}>{t('auth.codeSent', { email: email.trim() })}</p>
              <Button variant="quiet" disabled={busy} onClick={() => { setSent(false); setCode(''); setError(null); }}>{t('auth.changeEmail')}</Button>
            </>}
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            {!sent ? (
              <>
                <TextField label={t('auth.emailLabel')} value={email} onChange={setEmail} inputMode="email" autoComplete="email"
                  placeholder={t('auth.emailPlaceholder')} autoFocus />
                <Button disabled={busy} onClick={() => void send()}>{t('auth.sendCode')}</Button>
              </>
            ) : (
              <>
                <TextField label={t('auth.codeLabel')} value={code} onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, tuning.auth.otpLength))}
                  inputMode="numeric" autoComplete="one-time-code" maxLength={tuning.auth.otpLength} autoFocus />
                <Button disabled={busy || code.length !== tuning.auth.otpLength} onClick={() => void signIn()}>{busy ? t('auth.checking') : t('auth.signIn')}</Button>
                <Button variant="quiet" disabled={busy} onClick={() => void send()}>{t('auth.resend')}</Button>
              </>
            )}
            {error && <p role="alert" style={{ margin: 0, color: 'var(--flag-500)', fontWeight: 800 }}>{error}</p>}
            {/* Apple is offered on the email step only: once a code is out, the code step needs the room on a landscape phone, and
                "Use a different email" brings the choice back. */}
            {apple && !sent && <>
              <p style={{ margin: 0, textAlign: 'center' }}>{t('auth.or')}</p>
              <Button variant="quiet" onClick={() => void startApple()}>{t('auth.apple')}</Button>
            </>}
          </div>
        </div>
      </Panel>
    </div>
  );
}
export const screen: ScreenDef<'signIn'> = { id: 'signIn', layer: 'full', component: SignIn };
